import { useState, useEffect, useCallback } from 'react';
import { Category, useStore } from '../store/useStore';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { Plus, Trash2, Edit2, X, Copy, Shield, LayoutGrid, FolderDot, Loader2 } from 'lucide-react';
import { ConfigModal } from './ConfigModal';
import { supabase } from '../lib/supabase';

const EMPTY_FORM = { name: '', isGeneral: false, isArtistCategory: false, isPublic: false };

export function CategoriesConfig() {
  const { categories, addCategory, updateCategory, deleteCategory } = useVisibleStore();
  const { currentUser } = useStore();
  
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [initialData, setInitialData] = useState(EMPTY_FORM);
  const [modalOpen, setModalOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  
  const [categoryManagersMap, setCategoryManagersMap] = useState<Record<string, any[]>>({});
  const [newManagerEmails, setNewManagerEmails] = useState<Record<string, string>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [loadingManagers, setLoadingManagers] = useState(false);

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialData);
  const canManage = currentUser.role === 'admin' || (currentUser.role as string) === 'superadmin';

  const loadAllManagers = async () => {
    setLoadingManagers(true);
    try {
      const { data, error } = await supabase
        .from('category_managers')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: true });
      if (error) {
        console.error("Error loading category managers:", error);
      } else if (data) {
        const map: Record<string, any[]> = {};
        data.forEach((r: any) => {
          if (!map[r.category_id]) map[r.category_id] = [];
          map[r.category_id].push(r);
        });
        setCategoryManagersMap(map);
      }
    } catch (err) {
      console.error("Exception loading category managers:", err);
    } finally {
      setLoadingManagers(false);
    }
  };

  useEffect(() => {
    loadAllManagers();
  }, []);

  const openNew = () => {
    setFormData(EMPTY_FORM);
    setInitialData(EMPTY_FORM);
    setEditingId(null);
    setStatusMessage(null);
    setModalOpen(true);
  };

  const openEdit = (cat: Category) => {
    const data = { 
      name: cat.name, 
      isGeneral: cat.isGeneral || false, 
      isArtistCategory: cat.isArtistCategory || false,
      isPublic: cat.isPublic || false 
    };
    setFormData(data);
    setInitialData(data);
    setEditingId(cat.id);
    setStatusMessage(null);
    setModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setInitialData(EMPTY_FORM);
    setStatusMessage(null);
  }, []);

  const handleSubmit = () => {
    if (!formData.name.trim()) return;

    const isNameArtist = formData.name.toLowerCase().includes('artist') || 
                         formData.name.toLowerCase().includes('artiste');
    const isArtist = formData.isArtistCategory || isNameArtist;

    const finalData = {
      name: formData.name,
      isGeneral: isArtist ? true : (formData.isGeneral || formData.isPublic),
      isArtistCategory: isArtist,
      isPublic: formData.isPublic
    };

    if (editingId) {
      updateCategory(editingId, finalData);
    } else {
      addCategory(finalData);
    }
    setInitialData(finalData);
    setStatusMessage({ text: 'Catégorie enregistrée ✓', type: 'success' });
    setTimeout(() => { setStatusMessage(null); closeModal(); }, 1200);
  };

  const handleAddManager = async (categoryId: string) => {
    const emailVal = (newManagerEmails[categoryId] || '').trim().toLowerCase();
    try {
      const tokenVal = crypto.randomUUID().replace(/-/g, '');
      // Upsert: if manager was previously soft-deleted, restore them with a new token
      const { error } = await supabase
        .from('category_managers')
        .upsert({
          category_id: categoryId,
          email: emailVal || null,
          email_confirmed: false,
          token: tokenVal,
          deleted_at: null
        }, { onConflict: 'category_id,email', ignoreDuplicates: false });

      if (error) throw error;
      setNewManagerEmails(prev => ({ ...prev, [categoryId]: '' }));
      await loadAllManagers();
    } catch (err: any) {
      alert("Erreur lors de l'ajout du manager : " + err.message);
    }
  };

  const handleRevokeManager = async (managerId: string) => {
    if (!confirm("Voulez-vous vraiment révoquer l'accès de ce manager ? Le lien d'accès ne fonctionnera plus.")) return;
    try {
      const { error } = await supabase
        .from('category_managers')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', managerId);

      if (error) throw error;
      await loadAllManagers();
    } catch (err: any) {
      alert("Erreur lors de la révocation : " + err.message);
    }
  };

  const copyToClipboard = (token: string, id: string) => {
    const link = `${window.location.origin}/?manager_token=${token}`;
    navigator.clipboard.writeText(link);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="p-3.5 sm:p-6 md:p-8 h-full flex flex-col overflow-hidden relative bg-[#0A0B0E]">
      <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-indigo-600/10 rounded-full blur-[100px] pointer-events-none" />

      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start justify-between gap-4 relative z-10">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent flex items-center gap-2 sm:gap-3">
            <LayoutGrid className="text-emerald-400 w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
            <span className="truncate">Gestion des Catégories</span>
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm mt-1 sm:mt-2 max-w-2xl leading-relaxed">
            Créez des catégories et gérez les accès des managers de manière centralisée.
          </p>
        </div>
        {canManage && (
          <button
            onClick={openNew}
            className="w-full sm:w-auto flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2 sm:py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-semibold text-xs sm:text-sm rounded-xl shadow-lg shadow-indigo-500/20 transition-all"
          >
            <Plus className="w-4 h-4 flex-shrink-0" />
            <span>Nouvelle catégorie</span>
          </button>
        )}
      </div>

      {/* Category list */}
      <div className="flex-1 bg-transparent sm:bg-[#14161B]/50 backdrop-blur-none sm:backdrop-blur-sm border-0 sm:border border-[#2D3139] rounded-none sm:rounded-2xl overflow-y-auto p-0 sm:p-6 custom-scrollbar shadow-none sm:shadow-inner relative z-10">
        <div className="flex justify-between items-center mb-6 sticky top-0 bg-[#14161B] sm:bg-[#14161B]/90 backdrop-blur-md p-2 -mt-2 sm:-mt-2 sm:-mx-2 rounded-lg z-20">
          <h3 className="font-semibold text-lg text-white">Catégories Existantes</h3>
          <span className="bg-indigo-500/20 text-indigo-400 text-xs font-bold px-3 py-1 rounded-full border border-indigo-500/30">
            {categories.length} total
          </span>
        </div>

        {categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 space-y-4">
            <FolderDot className="w-16 h-16 opacity-20" />
            <p>Aucune catégorie configurée pour le moment.</p>
            {canManage && (
              <button onClick={openNew} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-semibold transition-colors">
                + Créer une catégorie
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {categories.map((cat) => {
              const managers = categoryManagersMap[cat.id] || [];

              return (
                <div key={cat.id} className="group relative bg-[#0A0B0E]/80 border border-[#2D3139] hover:border-indigo-500/50 p-5 rounded-2xl flex flex-col gap-4 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/10 hover:-translate-y-1 overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-bl-full -z-10 transition-transform duration-500 group-hover:scale-150" />

                  <div className="flex justify-between items-start z-10">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold shadow-inner ${cat.isGeneral ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 text-emerald-400' : 'bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 text-indigo-400'}`}>
                        {cat.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-bold text-white text-lg tracking-wide">{cat.name}</h4>
                        <div className="flex gap-1.5 flex-wrap mt-1">
                          {cat.isGeneral && (
                            <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 uppercase tracking-widest">Générale</span>
                          )}
                          {cat.isArtistCategory && (
                            <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30 uppercase tracking-widest">Artistes 🎨</span>
                          )}
                          {cat.isPublic && (
                            <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold bg-sky-500/20 text-sky-400 border border-sky-500/30 uppercase tracking-widest">Publique 👥</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity bg-[#14161B] p-1 rounded-lg border border-[#2D3139]">
                        <button onClick={() => openEdit(cat)} className="p-1.5 text-slate-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded transition-colors" title="Modifier">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) deleteCategory(cat.id); }}
                          className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors" title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Accès Managers Multiple */}
                  {(!cat.isGeneral || cat.isArtistCategory) && canManage && (
                    <div className="pt-4 border-t border-[#2D3139]/50 z-10 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-slate-400 flex items-center gap-1.5 uppercase tracking-wider">
                          <Shield className="w-3.5 h-3.5" /> Accès Managers
                        </p>
                      </div>

                      {/* Managers List */}
                      <div className="space-y-2">
                        {loadingManagers ? (
                          <div className="flex justify-center py-2">
                            <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
                          </div>
                        ) : managers.length === 0 ? (
                          <p className="text-xs text-slate-500 italic text-center py-1">
                            Aucun manager configuré.
                          </p>
                        ) : (
                          managers.map((m) => (
                            <div key={m.id} className="flex items-center justify-between gap-3 p-2 bg-[#050505] border border-[#2D3139]/60 rounded-xl">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold text-slate-300 truncate">
                                    {m.full_name || m.email || "Lien d'invitation généré"}
                                  </span>
                                  <span className={`px-1.5 py-0.5 text-[8px] font-bold rounded uppercase ${
                                    m.email_confirmed
                                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                      : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                  }`}>
                                    {m.email_confirmed ? 'Confirmé ✓' : 'En attente'}
                                  </span>
                                </div>
                                {m.full_name && m.email && (
                                  <p className="text-[10px] text-slate-500 truncate mt-0.5">{m.email}</p>
                                )}
                                {!m.email_confirmed && (
                                  <p className="text-[9px] text-slate-500 truncate mt-0.5 select-all">
                                    {`${window.location.origin}/?manager_token=${m.token.slice(0, 10)}...`}
                                  </p>
                                )}
                              </div>

                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => copyToClipboard(m.token, m.id)}
                                  className="p-1 bg-[#14161C] hover:bg-[#2D3139] text-slate-400 hover:text-white border border-[#2D3139]/60 rounded-lg transition-all"
                                  title="Copier le lien"
                                >
                                  {copiedId === m.id ? (
                                    <span className="text-[9px] text-emerald-400 font-bold px-1">Copié ✓</span>
                                  ) : (
                                    <Copy className="w-3 h-3" />
                                  )}
                                </button>

                                {canManage && (
                                  <button
                                    onClick={() => handleRevokeManager(m.id)}
                                    className="p-1 bg-[#14161C] hover:bg-red-500/10 text-slate-400 hover:text-red-400 border border-[#2D3139]/60 rounded-lg transition-all"
                                    title="Révoquer le manager"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Add Manager Form */}
                      {canManage && (
                        <div className="flex gap-2 pt-1.5">
                          <input
                            type="email"
                            value={newManagerEmails[cat.id] || ''}
                            onChange={(e) => setNewManagerEmails(prev => ({ ...prev, [cat.id]: e.target.value }))}
                            placeholder="Inviter manager par email (optionnel)"
                            className="flex-1 bg-[#050505] border border-[#2D3139] rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500"
                          />
                          <button
                            onClick={() => handleAddManager(cat.id)}
                            className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold transition-all"
                          >
                            Ajouter
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Category Form Modal */}
      <ConfigModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
        icon={editingId ? <Edit2 className="w-5 h-5" /> : <FolderDot className="w-5 h-5" />}
        isDirty={isDirty}
      >
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Nom de la catégorie</label>
            <input
              type="text"
              autoFocus
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all placeholder-slate-600"
              placeholder="Ex: Hospitality Team"
              value={formData.name}
              onChange={(e) => {
                const val = e.target.value;
                const isArt = val.toLowerCase().includes('artist') || val.toLowerCase().includes('artiste');
                setFormData({
                  ...formData,
                  name: val,
                  isGeneral: isArt ? true : formData.isGeneral,
                  isArtistCategory: isArt ? true : formData.isArtistCategory
                });
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            />
          </div>

          <div className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div className="relative flex items-center justify-center">
                <input
                  type="checkbox" id="isGeneralModal"
                  className="peer appearance-none w-5 h-5 border-2 border-[#2D3139] rounded bg-transparent checked:bg-emerald-500 checked:border-emerald-500 cursor-pointer transition-all"
                  checked={formData.isGeneral}
                  onChange={(e) => setFormData({ ...formData, isGeneral: e.target.checked })}
                />
                <div className="absolute pointer-events-none opacity-0 peer-checked:opacity-100 text-white">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <label htmlFor="isGeneralModal" className="text-sm font-medium text-white cursor-pointer">Catégorie Générale</label>
            </div>
            <p className="text-xs text-slate-500 mt-2 ml-8">Visible par tous les managers dans le planning.</p>
          </div>

          <div className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div className="relative flex items-center justify-center">
                <input
                  type="checkbox" id="isArtistCategoryModal"
                  className="peer appearance-none w-5 h-5 border-2 border-[#2D3139] rounded bg-transparent checked:bg-amber-500 checked:border-amber-500 cursor-pointer transition-all"
                  checked={formData.isArtistCategory}
                  onChange={(e) => setFormData({ ...formData, isArtistCategory: e.target.checked })}
                />
                <div className="absolute pointer-events-none opacity-0 peer-checked:opacity-100 text-white">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <label htmlFor="isArtistCategoryModal" className="text-sm font-medium text-white cursor-pointer font-semibold text-amber-400">Catégorie d'Artistes 🎨</label>
            </div>
            <p className="text-xs text-slate-500 mt-2 ml-8">Tous les membres de cette catégorie seront marqués comme Artistes. Active la gestion des scènes et catering.</p>
          </div>

          <div className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div className="relative flex items-center justify-center">
                <input
                  type="checkbox" id="isPublicModal"
                  className="peer appearance-none w-5 h-5 border-2 border-[#2D3139] rounded bg-transparent checked:bg-sky-500 checked:border-sky-500 cursor-pointer transition-all"
                  checked={formData.isPublic}
                  onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                />
                <div className="absolute pointer-events-none opacity-0 peer-checked:opacity-100 text-white">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <label htmlFor="isPublicModal" className="text-sm font-medium text-white cursor-pointer font-semibold text-sky-400">Catégorie Publique 👥</label>
            </div>
            <p className="text-xs text-slate-500 mt-2 ml-8">Les shifts et la timetable des points de cette catégorie sont visibles par tous le monde (team, artistes, managers). Pour les scènes : activez ceci pour rendre la programmation publique.</p>
          </div>

          {statusMessage && (
            <div className={`p-3 rounded-xl text-sm font-medium text-center ${statusMessage.type === 'success' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
              {statusMessage.text}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={closeModal} className="flex-1 px-4 py-3 border border-[#2D3139] hover:bg-[#1A1D23] text-white rounded-xl transition-all flex items-center justify-center gap-2 font-medium">
              <X className="w-4 h-4" /> Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={!formData.name.trim()}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 disabled:opacity-50 text-white rounded-xl transition-all shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2 font-medium"
            >
              {editingId ? <Edit2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {editingId ? 'Mettre à jour' : 'Ajouter'}
            </button>
          </div>
        </div>
      </ConfigModal>
    </div>
  );
}
